import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { apiClient } from '@/api/client';

interface TestSignalModalProps {
  workflowId: string;
  triggerConfig: any;
  open: boolean;
  onClose: () => void;
  onRunTest: (customSignal?: Record<string, any>) => void;
  loading?: boolean;
}

type FieldDef = { key: string; label: string; type?: 'select' | 'combobox'; options?: string[]; dynamicKey?: string };

const SOURCE_FIELDS: Record<string, FieldDef[]> = {
  kubernetes: [
    { key: 'cluster_id', label: 'Cluster', type: 'combobox', dynamicKey: 'clusters' },
    { key: 'namespace', label: 'Namespace', type: 'combobox', dynamicKey: 'namespaces' },
    { key: 'workload_name', label: 'Workload Name', type: 'combobox', dynamicKey: 'workloads' },
    { key: 'resource_name', label: 'Pod / Resource Name' },
    { key: 'reason', label: 'Reason', type: 'combobox', options: ['CrashLoopBackOff', 'OOMKilled', 'ImagePullBackOff', 'Error', 'Evicted', 'FailedScheduling'] },
    { key: 'severity', label: 'Severity', type: 'select', options: ['critical', 'high', 'medium', 'low', 'info'] },
  ],
  aws: [
    { key: 'aws_account_id', label: 'AWS Account', type: 'combobox', dynamicKey: 'aws_accounts' },
    { key: 'region', label: 'Region', type: 'combobox', options: ['us-east-1', 'us-west-2', 'eu-west-1', 'eu-central-1', 'ap-southeast-1', 'ap-northeast-1', 'us-west-1'] },
    { key: 'service_name', label: 'AWS Service', type: 'combobox', options: ['iam', 'ec2', 's3', 'rds', 'lambda', 'ecs', 'dynamodb', 'kms', 'secretsmanager'] },
    { key: 'resource_name', label: 'Resource ARN' },
    { key: 'incident_type', label: 'Incident Type', type: 'combobox', options: ['unauthorized_access', 'resource_misconfiguration', 'data_exfiltration', 'privilege_escalation', 'unusual_activity', 'cost_anomaly', 'compliance_violation', 'service_disruption'] },
    { key: 'event_name', label: 'Event Name' },
    { key: 'severity', label: 'Severity', type: 'select', options: ['critical', 'high', 'medium', 'low'] },
  ],
  pagerduty: [
    { key: 'service_name', label: 'Service Name', type: 'combobox', dynamicKey: 'pagerduty_services' },
    { key: 'title', label: 'Incident Title' },
    { key: 'urgency', label: 'Urgency', type: 'select', options: ['high', 'low'] },
    { key: 'severity', label: 'Severity', type: 'select', options: ['critical', 'error', 'warning', 'info'] },
    { key: 'pagerduty_incident_id', label: 'Incident ID' },
  ],
  github: [
    { key: 'repo', label: 'Repository (org/repo)', type: 'combobox', dynamicKey: 'github_repos' },
    { key: 'action_name', label: 'Workflow Name', type: 'combobox', dynamicKey: 'github_workflows' },
    { key: 'branch', label: 'Branch', type: 'combobox', dynamicKey: 'github_branches' },
    { key: 'pr_title', label: 'PR Title' },
    { key: 'conclusion', label: 'Conclusion', type: 'select', options: ['success', 'failure', 'cancelled'] },
    { key: 'commit_sha', label: 'Commit SHA' },
    { key: 'sender', label: 'Sender' },
  ],
  posthog: [
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['$exception', '$rageclick', '$log_entry', 'log_alert'] },
    { key: 'session_id', label: 'Session ID' },
    { key: 'distinct_id', label: 'Distinct ID' },
  ],
  vercel: [
    { key: 'project_name', label: 'Project Name', type: 'combobox', dynamicKey: 'vercel_projects' },
    { key: 'deployment_id', label: 'Deployment ID' },
    { key: 'error_message', label: 'Error Message' },
    { key: 'target', label: 'Target', type: 'select', options: ['production', 'preview'] },
    { key: 'git_branch', label: 'Git Branch' },
  ],
  railway: [
    { key: 'project_name', label: 'Project Name', type: 'combobox', dynamicKey: 'railway_projects' },
    { key: 'service_name', label: 'Service Name' },
    { key: 'environment_name', label: 'Environment', type: 'combobox', options: ['production', 'staging', 'development'] },
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['Deployment.failed', 'Deployment.crashed', 'Deployment.succeeded'] },
    { key: 'status', label: 'Status', type: 'select', options: ['FAILED', 'CRASHED', 'SUCCESS', 'DEPLOYING'] },
    { key: 'git_branch', label: 'Git Branch' },
  ],
  flyio: [
    { key: 'app_name', label: 'App Name', type: 'combobox', dynamicKey: 'flyio_apps' },
    { key: 'machine_id', label: 'Machine ID' },
    { key: 'machine_name', label: 'Machine Name' },
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['machine.crashed', 'machine.stopped', 'machine.started', 'app.down'] },
    { key: 'status', label: 'Status', type: 'select', options: ['started', 'stopped', 'suspended', 'created', 'destroyed'] },
    { key: 'region', label: 'Region' },
    { key: 'exit_code', label: 'Exit Code' },
  ],
  databricks: [
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['job_run.failed', 'job_run.succeeded', 'job_run.long_running', 'cluster.terminated_unexpectedly', 'pipeline.update_failed'] },
    { key: 'job_id', label: 'Job', type: 'combobox', dynamicKey: 'databricks_jobs' },
    { key: 'job_name', label: 'Job Name' },
    { key: 'run_id', label: 'Run ID' },
    { key: 'result_state', label: 'Result State', type: 'select', options: ['FAILED', 'SUCCESS', 'TIMEDOUT', 'CANCELED'] },
    { key: 'error_message', label: 'Error Message' },
    { key: 'cluster_id', label: 'Cluster ID' },
    { key: 'cluster_name', label: 'Cluster Name' },
    { key: 'pipeline_id', label: 'Pipeline ID' },
    { key: 'pipeline_name', label: 'Pipeline Name' },
  ],
  nebius: [
    { key: 'project_id', label: 'Project ID' },
    { key: 'instance_id', label: 'Instance ID' },
    { key: 'instance_name', label: 'Instance Name' },
    { key: 'cluster_id', label: 'Cluster ID' },
    { key: 'node_group_id', label: 'Node Group ID' },
    { key: 'node_name', label: 'Node Name' },
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['node.gpu_error', 'node.maintenance_scheduled', 'node.not_ready', 'instance.stopped'] },
    { key: 'condition', label: 'Condition' },
    { key: 'status', label: 'Status' },
  ],
  beam: [
    { key: 'deployment_id', label: 'Deployment ID' },
    { key: 'deployment_name', label: 'Deployment Name' },
    { key: 'container_id', label: 'Container ID' },
    { key: 'task_id', label: 'Task ID' },
    { key: 'machine_id', label: 'Machine ID' },
    { key: 'pool_name', label: 'Pool Name' },
    { key: 'sandbox_id', label: 'Sandbox ID' },
    { key: 'exit_code', label: 'Exit Code' },
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['task.failed', 'task.timed_out', 'container.stopped', 'deployment.scaled_to_zero', 'queue.depth_high', 'machine.down', 'sandbox.execution_failed', 'sandbox.terminated'] },
    { key: 'status', label: 'Status' },
  ],
  daytona: [
    { key: 'sandbox_id', label: 'Sandbox ID' },
    { key: 'sandbox_name', label: 'Sandbox Name' },
    { key: 'snapshot', label: 'Snapshot' },
    { key: 'volume', label: 'Volume' },
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['sandbox.created', 'sandbox.stopped', 'sandbox.error', 'sandbox.archived', 'sandbox.execution_failed', 'snapshot.build_failed', 'volume.error'] },
    { key: 'state', label: 'State' },
  ],
  supabase: [
    { key: 'project_ref', label: 'Project Ref' },
    { key: 'project_name', label: 'Project Name' },
    { key: 'service', label: 'Service' },
    { key: 'branch_id', label: 'Branch ID' },
    { key: 'table', label: 'Table' },
    { key: 'op', label: 'Operation', type: 'select', options: ['INSERT', 'UPDATE', 'DELETE'] },
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['project.health_degraded', 'backup.failed', 'replica.unhealthy', 'usage.threshold', 'branch.created', 'branch.merge_failed', 'db.row_event'] },
    { key: 'state', label: 'State' },
  ],
  planetscale: [
    { key: 'database', label: 'Database' },
    { key: 'branch', label: 'Branch' },
    { key: 'deploy_request_number', label: 'Deploy Request Number' },
    { key: 'deploy_request_state', label: 'Deploy Request State' },
    { key: 'organization', label: 'Organization' },
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['deploy_request.opened', 'deploy_request.queued', 'deploy_request.in_progress', 'deploy_request.schema_applied', 'deploy_request.errored', 'deploy_request.reverted', 'deploy_request.closed', 'branch.ready', 'branch.anomaly', 'branch.primary_promoted', 'branch.sleeping', 'cluster.storage', 'backup.completed', 'backup.failed'] },
    { key: 'state', label: 'State' },
  ],
  neon: [
    { key: 'project_id', label: 'Project ID' },
    { key: 'project_name', label: 'Project Name' },
    { key: 'branch_id', label: 'Branch ID' },
    { key: 'branch_name', label: 'Branch Name' },
    { key: 'endpoint_id', label: 'Endpoint ID' },
    { key: 'operation_action', label: 'Operation Action' },
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['branch.created', 'branch.ready', 'operation.failed', 'compute.suspended', 'compute.active', 'usage.threshold'] },
    { key: 'state', label: 'State' },
  ],
  clickhouse: [
    { key: 'service_id', label: 'Service ID' },
    { key: 'service_name', label: 'Service Name' },
    { key: 'clickpipe_id', label: 'ClickPipe ID' },
    { key: 'clickpipe_name', label: 'ClickPipe Name' },
    { key: 'backup_id', label: 'Backup ID' },
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['service.state_changed', 'service.idle', 'service.scaled', 'service.version_changed', 'backup.completed', 'backup.failed', 'query.error_spike', 'clickpipe.failed', 'usage.threshold', 'service.too_many_parts', 'service.high_query_concurrency'] },
    { key: 'state', label: 'State' },
    { key: 'previous_state', label: 'Previous State' },
  ],
  jenkins: [
    { key: 'job_name', label: 'Job Name', type: 'combobox', dynamicKey: 'jenkins_jobs' },
    { key: 'build_number', label: 'Build Number' },
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['build.started', 'build.completed', 'build.queued'] },
    { key: 'build_status', label: 'Build Status', type: 'select', options: ['SUCCESS', 'FAILURE', 'UNSTABLE', 'ABORTED'] },
    { key: 'build_url', label: 'Build URL' },
  ],
  circleci: [
    { key: 'project_slug', label: 'Project', type: 'combobox', dynamicKey: 'circleci_projects' },
    { key: 'branch', label: 'Branch' },
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['workflow-completed', 'job-completed'] },
    { key: 'status', label: 'Status', type: 'select', options: ['success', 'failed', 'error', 'canceled', 'unauthorized'] },
    { key: 'workflow_name', label: 'Workflow Name' },
    { key: 'job_name', label: 'Job Name' },
  ],
  sonarcloud: [
    { key: 'project_key', label: 'Project', type: 'combobox', dynamicKey: 'sonarcloud_projects' },
    { key: 'branch', label: 'Branch' },
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['analysis.completed', 'analysis.failed'] },
    { key: 'quality_gate_status', label: 'Quality Gate Status', type: 'select', options: ['OK', 'ERROR', 'NONE'] },
    { key: 'project_name', label: 'Project Name' },
  ],
  okta: [
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['user.account.lock', 'user.account.report_suspicious_activity_by_enduser', 'security.threat.detected', 'user.account.privilege.grant', 'user.mfa.factor.deactivate', 'user.mfa.factor.reset_all', 'user.lifecycle.create', 'user.lifecycle.deactivate', 'user.lifecycle.suspend', 'group.user_membership.add', 'group.user_membership.remove', 'application.user_membership.add', 'application.user_membership.remove'] },
    { key: 'target_user', label: 'Target User', type: 'combobox', dynamicKey: 'okta_users' },
    { key: 'actor', label: 'Actor', type: 'combobox', dynamicKey: 'okta_users' },
    { key: 'target_group', label: 'Target Group', type: 'combobox', dynamicKey: 'okta_groups' },
    { key: 'outcome', label: 'Outcome', type: 'select', options: ['SUCCESS', 'FAILURE'] },
    { key: 'client_ip', label: 'Client IP' },
    { key: 'client_location', label: 'Client Location' },
  ],
  terraform: [
    { key: 'workspace', label: 'Workspace', type: 'combobox', dynamicKey: 'terraform_workspaces' },
    { key: 'organization', label: 'Organization' },
    { key: 'run_id', label: 'Run ID' },
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['run:created', 'run:planning', 'run:needs_attention', 'run:applying', 'run:completed', 'run:errored', 'assessment:drifted', 'assessment:check_failure', 'assessment:failed'] },
    { key: 'run_status', label: 'Run Status', type: 'select', options: ['pending', 'planning', 'planned', 'planned_and_finished', 'applying', 'applied', 'errored', 'discarded', 'canceled'] },
    { key: 'created_by', label: 'Created By' },
  ],
  pulumi: [
    { key: 'stack', label: 'Stack', type: 'combobox', dynamicKey: 'pulumi_stacks' },
    { key: 'organization', label: 'Organization' },
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['stack_created', 'stack_deleted', 'update_succeeded', 'update_failed', 'preview_succeeded', 'preview_failed', 'refresh_succeeded', 'refresh_failed', 'destroy_succeeded', 'destroy_failed', 'deployment_started', 'deployment_succeeded', 'deployment_failed', 'drift_detected', 'drift_detection_succeeded', 'drift_detection_failed', 'drift_remediation_succeeded', 'drift_remediation_failed', 'policy_violation_mandatory', 'policy_violation_advisory'] },
    { key: 'operation', label: 'Operation', type: 'select', options: ['update', 'preview', 'refresh', 'destroy', 'detect-drift', 'remediate-drift'] },
    { key: 'update_version', label: 'Update Version' },
    { key: 'user', label: 'User' },
  ],
  vault: [
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['seal.sealed', 'seal.unsealed', 'health.degraded', 'secret.version_created', 'secret.stale', 'policy.created', 'policy.deleted', 'auth_method.enabled', 'auth_method.disabled'] },
    { key: 'mount', label: 'Mount', type: 'combobox', dynamicKey: 'vault_mounts' },
    { key: 'secret_path', label: 'Secret Path' },
    { key: 'secret_version', label: 'Secret Version' },
    { key: 'secret_age_days', label: 'Secret Age (Days)' },
    { key: 'policy_name', label: 'Policy Name' },
    { key: 'auth_method', label: 'Auth Method' },
  ],
  infisical: [
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['secret.created', 'secret.updated', 'secret.deleted', 'approval.requested', 'sync.failed', 'identity.created'] },
    { key: 'project_name', label: 'Project', type: 'combobox', dynamicKey: 'infisical_projects' },
    { key: 'environment', label: 'Environment', type: 'select', options: ['dev', 'staging', 'prod'] },
    { key: 'secret_path', label: 'Secret Path' },
    { key: 'secret_key', label: 'Secret Key' },
    { key: 'sync_name', label: 'Sync Name' },
    { key: 'actor', label: 'Actor' },
  ],
  datadog: [
    { key: 'monitor_name', label: 'Monitor Name' },
    { key: 'monitor_id', label: 'Monitor ID' },
    { key: 'alert_status', label: 'Alert Status', type: 'select', options: ['Alert', 'Warn', 'OK', 'No Data'] },
    { key: 'previous_status', label: 'Previous Status', type: 'select', options: ['Alert', 'Warn', 'OK', 'No Data'] },
    { key: 'monitor_type', label: 'Monitor Type', type: 'combobox', options: ['metric alert', 'service check', 'event alert', 'log alert', 'process alert', 'synthetics alert', 'rum alert', 'audit alert', 'composite'] },
  ],
  cloudflare: [
    { key: 'zone_name', label: 'Zone', type: 'combobox', dynamicKey: 'cloudflare_zones' },
    { key: 'event_type', label: 'Event Type', type: 'select', options: ['security_event', 'health_check_failure', 'worker_failure', 'ssl_expiring'] },
    { key: 'worker_name', label: 'Worker Name' },
    { key: 'source_ip', label: 'Source IP' },
    { key: 'description', label: 'Description' },
    { key: 'severity', label: 'Severity', type: 'select', options: ['critical', 'high', 'medium', 'low', 'info'] },
  ],
  request: [
    { key: 'category', label: 'Category', type: 'select', options: ['k8s', 'cloud', 'cloudflare', 'pagerduty', 'datadog', 'argocd', 'argo-rollouts', 'fluxcd', 'karpenter', 'kyverno', 'trivy', 'github', 'gitlab', 'helm', 'vercel', 'terraform', 'general'] },
    { key: 'namespace', label: 'Namespace', type: 'combobox', dynamicKey: 'namespaces' },
    { key: 'resource_name', label: 'Resource Name' },
    { key: 'requester', label: 'Requester Name', type: 'combobox', dynamicKey: 'kestrel_users' },
    { key: 'prompt', label: 'User Prompt' },
  ],
  schedule: [
    { key: 'schedule_interval', label: 'Schedule Interval', type: 'select', options: ['hourly', 'daily', 'weekly', 'monthly'] },
    { key: 'fired_at', label: 'Fired At (RFC3339)' },
    { key: 'fired_date', label: 'Fired Date (YYYY-MM-DD)' },
    { key: 'message', label: 'Message' },
  ],
  karpenter: [
    { key: 'event_type', label: 'Signal Type', type: 'select', options: ['nodeclaim.provisioning_failed', 'node.interrupted', 'nodepool.limit_reached'] },
    { key: 'nodepool_name', label: 'NodePool Name' },
    { key: 'nodeclaim_name', label: 'NodeClaim Name' },
    { key: 'node_name', label: 'Node Name' },
    { key: 'reason', label: 'Reason' },
    { key: 'message', label: 'Message' },
  ],
  kyverno: [
    { key: 'kyverno_signal_type', label: 'Signal Type', type: 'select', options: ['policy.violation', 'policy.admission_blocked'] },
    { key: 'policy_name', label: 'Policy Name' },
    { key: 'rule_name', label: 'Rule Name' },
    { key: 'namespace', label: 'Namespace', type: 'combobox', dynamicKey: 'namespaces' },
    { key: 'resource_kind', label: 'Resource Kind' },
    { key: 'resource_name', label: 'Resource Name' },
    { key: 'severity', label: 'Severity', type: 'select', options: ['critical', 'high', 'medium', 'low', 'info'] },
    { key: 'message', label: 'Message' },
  ],
  trivy: [
    { key: 'trivy_signal_type', label: 'Signal Type', type: 'select', options: ['vulnerability.detected', 'secret.exposed', 'configaudit.failed', 'compliance.failed'] },
    { key: 'namespace', label: 'Namespace', type: 'combobox', dynamicKey: 'namespaces' },
    { key: 'workload_kind', label: 'Workload Kind' },
    { key: 'workload_name', label: 'Workload Name' },
    { key: 'image', label: 'Image' },
    { key: 'severity', label: 'Severity', type: 'select', options: ['critical', 'high', 'medium', 'low'] },
    { key: 'message', label: 'Message' },
  ],
  custom_webhook: [
    { key: 'event_type', label: 'Event Type' },
  ],
};

// AWS cost triggers (from the cloud cost pollers) carry cost-specific payloads,
// not the CloudTrail/security fields — mirror server-side buildAWSCostTestMockSignal.
const AWS_COST_SIGNAL_TYPES = new Set(['cost_anomaly', 'budget_alert', 'forecast_overrun', 'spend_spike', 'idle_resource']);

const AWS_COST_FIELDS: FieldDef[] = [
  { key: 'aws_account_id', label: 'AWS Account', type: 'combobox', dynamicKey: 'aws_accounts' },
  { key: 'event_type', label: 'Signal Type', type: 'select', options: ['cost_anomaly', 'budget_alert', 'forecast_overrun', 'spend_spike', 'idle_resource'] },
  { key: 'impact_amount', label: 'Anomaly Impact ($)' },
  { key: 'budget_name', label: 'Budget Name' },
  { key: 'projected_total', label: 'Projected Month Total ($)' },
  { key: 'overrun_percent', label: 'Overrun %' },
  { key: 'yesterday_spend', label: 'Yesterday Spend ($)' },
  { key: 'spike_percent', label: 'Spike %' },
  { key: 'estimated_monthly_savings', label: 'Estimated Monthly Savings ($)' },
  { key: 'severity', label: 'Severity', type: 'select', options: ['critical', 'high', 'medium', 'low'] },
];

// GCP cost triggers are all poll-derived, so their test payload mirrors what the
// GCP cost poller synthesizes rather than any webhook shape.
const GCP_FIELDS: FieldDef[] = [
  { key: 'gcp_project_id', label: 'GCP Project', type: 'combobox', dynamicKey: 'gcp_projects' },
  { key: 'event_type', label: 'Signal Type', type: 'select', options: ['cost_anomaly', 'budget_alert', 'forecast_overrun', 'spend_spike', 'idle_resource'] },
  { key: 'service_name', label: 'GCP Service' },
  { key: 'impact_amount', label: 'Anomaly Impact ($)' },
  { key: 'budget_name', label: 'Budget Name' },
  { key: 'budget_consumed_percent', label: 'Budget Consumed %' },
  { key: 'projected_total', label: 'Projected Month Total ($)' },
  { key: 'overrun_percent', label: 'Overrun %' },
  { key: 'yesterday_spend', label: 'Yesterday Spend ($)' },
  { key: 'spike_percent', label: 'Spike %' },
  { key: 'estimated_monthly_savings', label: 'Estimated Monthly Savings ($)' },
  { key: 'severity', label: 'Severity', type: 'select', options: ['critical', 'high', 'medium', 'low'] },
];

export function TestSignalModal({ workflowId, triggerConfig, open, onClose, onRunTest, loading }: TestSignalModalProps) {
  const [useDefaults, setUseDefaults] = useState(true);
  const [fields, setFields] = useState<Record<string, any>>({});
  const [defaultFields, setDefaultFields] = useState<Record<string, any>>({});
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, string[]>>({});
  const [catalogData, setCatalogData] = useState<any>(null);

  const source = triggerConfig?.source || 'kubernetes';
  const triggerSignalType = triggerConfig?.signals?.[0]?.signal_type || '';
  const fieldDefs =
    source === 'aws' && AWS_COST_SIGNAL_TYPES.has(triggerSignalType)
      ? AWS_COST_FIELDS
      : source === 'gcp'
      ? GCP_FIELDS
      : SOURCE_FIELDS[source] || SOURCE_FIELDS.kubernetes;

  useEffect(() => {
    if (open) {
      setLoadingDefaults(true);
      Promise.all([
        apiClient.getWorkflowTestDefaults(workflowId),
        apiClient.getWorkflowCatalog(),
      ]).then(([defaults, catalog]) => {
        setFields(defaults.fields || {});
        setDefaultFields(defaults.fields || {});
        // Build dynamic options from catalog
        const opts: Record<string, string[]> = {};
        if (catalog.clusters) opts.clusters = catalog.clusters.map((c: any) => c.name);
        if (catalog.namespaces) opts.namespaces = Array.from(new Set(catalog.namespaces.map((n: any) => n.name)));
        if (catalog.workloads) opts.workloads = Array.from(new Set(catalog.workloads.map((w: any) => w.name)));
        if (catalog.github_repos) opts.github_repos = catalog.github_repos;
        if (catalog.github_workflows) opts.github_workflows = Array.from(new Set(catalog.github_workflows.map((w: any) => w.file)));
        if (catalog.pagerduty_services) opts.pagerduty_services = catalog.pagerduty_services.map((s: any) => s.name);
        if (catalog.vercel_projects) opts.vercel_projects = catalog.vercel_projects.map((p: any) => p.name);
        if (catalog.railway_projects) opts.railway_projects = catalog.railway_projects.map((p: any) => p.name);
        if (catalog.flyio_apps) opts.flyio_apps = catalog.flyio_apps.map((a: any) => a.name);
        if (catalog.cloudflare_zones) opts.cloudflare_zones = catalog.cloudflare_zones.map((z: any) => z.name);
        if (catalog.terraform_workspaces) opts.terraform_workspaces = catalog.terraform_workspaces.map((w: any) => w.name || w.key);
        if (catalog.databricks_jobs) opts.databricks_jobs = catalog.databricks_jobs.map((j: any) => j.key || j.name);
        if (catalog.pulumi_stacks) opts.pulumi_stacks = catalog.pulumi_stacks.map((s: any) => s.name || s.key);
        if (catalog.vault_mounts) opts.vault_mounts = catalog.vault_mounts.map((m: any) => m.name || m.key);
        if (catalog.infisical_projects) opts.infisical_projects = catalog.infisical_projects.map((p: any) => p.name || p.key);
        if (catalog.jenkins_jobs) opts.jenkins_jobs = catalog.jenkins_jobs.map((j: any) => j.name || j.id);
        if (catalog.circleci_projects) opts.circleci_projects = catalog.circleci_projects.map((p: any) => p.id || p.name);
        if (catalog.sonarcloud_projects) opts.sonarcloud_projects = catalog.sonarcloud_projects.map((p: any) => p.key || p.name);
        if (catalog.okta_users) opts.okta_users = catalog.okta_users.map((u: any) => u.key || u.name);
        if (catalog.okta_groups) opts.okta_groups = catalog.okta_groups.map((g: any) => g.name || g.key);
        if (catalog.aws_accounts) opts.aws_accounts = catalog.aws_accounts.map((a: any) => a.account_id);
        if (catalog.gcp_projects) opts.gcp_projects = catalog.gcp_projects.map((p: any) => p.key);
        if (catalog.kestrel_users) opts.kestrel_users = catalog.kestrel_users.map((u: any) => u.name || u.email);
        setDynamicOptions(opts);
        // Store cluster-scoped data for reactive filtering
        setCatalogData(catalog);
        // Pre-fetch branches if default repo is set
        const defaultRepo = defaults.fields?.repo;
        if (defaultRepo && source === 'github') {
          apiClient.getGitHubBranches(defaultRepo).then(data => {
            setDynamicOptions(prev => ({ ...prev, github_branches: data.branches || [] }));
          }).catch(() => {});
        }
      }).catch(() => {
        setFields({});
      }).finally(() => setLoadingDefaults(false));
    }
  }, [open, workflowId]);

  if (!open) return null;

  // Get scoped options filtered by parent field selections
  const getOptionsForField = (dynamicKey: string): string[] => {
    if (!dynamicKey) return [];

    // Kubernetes: Cluster → Namespace → Workload
    if (dynamicKey === 'namespaces' && catalogData?.namespaces && fields.cluster_id) {
      const clusterId = catalogData.clusters?.find((c: any) => c.name === fields.cluster_id)?.id || fields.cluster_id;
      const filtered = catalogData.namespaces.filter((n: any) => n.cluster_id === clusterId).map((n: any) => n.name);
      return filtered.length > 0 ? Array.from(new Set(filtered)) : (dynamicOptions[dynamicKey] || []);
    }
    if (dynamicKey === 'workloads' && catalogData?.workloads && fields.cluster_id) {
      const clusterId = catalogData.clusters?.find((c: any) => c.name === fields.cluster_id)?.id || fields.cluster_id;
      const ns = fields.namespace;
      let filtered = catalogData.workloads.filter((w: any) => w.cluster_id === clusterId);
      if (ns) filtered = filtered.filter((w: any) => w.namespace === ns);
      const names = filtered.map((w: any) => w.name);
      return names.length > 0 ? Array.from(new Set(names)) : (dynamicOptions[dynamicKey] || []);
    }

    // GitHub: Repo → Workflow Name
    if (dynamicKey === 'github_workflows' && catalogData?.github_workflows && fields.repo) {
      const filtered = catalogData.github_workflows
        .filter((w: any) => w.repo === fields.repo)
        .map((w: any) => w.file);
      return filtered.length > 0 ? Array.from(new Set(filtered)) : (dynamicOptions[dynamicKey] || []);
    }

    // GitHub: Repo → Branches (fetched on-demand, stored in dynamicOptions)
    if (dynamicKey === 'github_branches') {
      return dynamicOptions.github_branches || [];
    }

    return dynamicOptions[dynamicKey] || [];
  };

  const handleFieldChange = (key: string, value: string) => {
    setFields(prev => {
      const next = { ...prev, [key]: value };
      // Kubernetes: Cluster → Namespace → Workload
      if (key === 'cluster_id') {
        delete next.namespace;
        delete next.workload_name;
      }
      if (key === 'namespace') {
        delete next.workload_name;
      }
      // GitHub: Repo → Branch, Workflow Name
      if (key === 'repo') {
        delete next.action_name;
        delete next.branch;
        // Fetch branches for the selected repo
        if (value) {
          apiClient.getGitHubBranches(value).then(data => {
            setDynamicOptions(prev => ({ ...prev, github_branches: data.branches || [] }));
          }).catch(() => {});
        } else {
          setDynamicOptions(prev => ({ ...prev, github_branches: [] }));
        }
      }
      return next;
    });
  };

  const handleRun = () => {
    if (useDefaults) {
      onRunTest(undefined);
    } else {
      const signal = { ...fields };
      if (signal.cluster_id && catalogData?.clusters) {
        const match = catalogData.clusters.find((c: any) => c.name === signal.cluster_id);
        if (match) signal.cluster_id = match.id;
      }
      onRunTest(signal);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background border rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col m-4" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b shrink-0">
          <h3 className="text-base font-semibold">Test Workflow</h3>
          <p className="text-sm text-muted-foreground mt-1">Configure the trigger signal values for this test execution.</p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="p-3 bg-muted/50 border border-border rounded-lg">
            <p className="text-xs text-muted-foreground">All workflow actions will execute live (Slack messages, Jira tickets, etc.)</p>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Use default test values</Label>
            <Switch checked={useDefaults} onCheckedChange={(checked) => {
              setUseDefaults(checked);
              if (checked) {
                setFields({ ...defaultFields });
              } else {
                setFields({});
              }
            }} />
          </div>

          {loadingDefaults ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className={`space-y-3 ${useDefaults ? 'opacity-50 pointer-events-none' : ''}`}>
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                Signal Fields ({source})
              </p>
              {fieldDefs.map(field => (
                <div key={field.key}>
                  <Label className="text-xs font-medium">{field.label}</Label>
                  {field.type === 'select' && field.options ? (
                    <Select value={fields[field.key] || ''} onValueChange={v => handleFieldChange(field.key, v)}>
                      <SelectTrigger className="mt-1 h-8 text-sm">
                        <SelectValue placeholder={`Select ${field.label.toLowerCase()}...`} />
                      </SelectTrigger>
                      <SelectContent className="z-[200]">
                        {field.options.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : field.type === 'combobox' ? (
                    <ComboboxInput
                      value={fields[field.key] || ''}
                      onChange={v => handleFieldChange(field.key, v)}
                      options={field.dynamicKey ? getOptionsForField(field.dynamicKey) : (field.options || [])}
                      placeholder={field.label}
                    />
                  ) : (
                    <Input
                      value={fields[field.key] || ''}
                      onChange={e => handleFieldChange(field.key, e.target.value)}
                      className="mt-1 h-8 text-sm"
                      placeholder={field.label}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t shrink-0">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleRun} disabled={loading}>
            {loading && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
            Run Test
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ComboboxInput({ value, onChange, options, placeholder }: { value: string; onChange: (v: string) => void; options: string[]; placeholder: string }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const filtered = value
    ? options.filter(o => o.toLowerCase().includes(value.toLowerCase()))
    : options;

  const updatePos = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  };

  return (
    <div className="relative mt-1">
      <Input
        ref={inputRef}
        value={value}
        onChange={e => { onChange(e.target.value); setShowDropdown(true); updatePos(); }}
        onFocus={() => { setShowDropdown(true); updatePos(); }}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        className="h-8 text-sm"
        placeholder={placeholder}
      />
      {showDropdown && filtered.length > 0 && createPortal(
        <div
          className="fixed z-[200] max-h-40 overflow-y-auto bg-popover border border-border rounded-md shadow-md"
          style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
        >
          {filtered.slice(0, 20).map(opt => (
            <button
              key={opt}
              type="button"
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent truncate"
              onMouseDown={e => { e.preventDefault(); onChange(opt); setShowDropdown(false); }}
            >
              {opt}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
